import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Euro,
  Calendar,
  Clock,
  Star,
  AlertTriangle,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  UserX,
  Target,
  Award,
  BarChart3
} from 'lucide-react';
import { collection, getDocs, query, where, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { toast } from 'react-hot-toast';

// Interfaces
interface MonthlyData {
  month: string;
  users: number;
  revenue: number;
  classes: number;
  newUsers: number;
}

interface ClassPopularity {
  name: string;
  totalEnrollments: number;
  averageOccupancy: number;
  waitlistCount: number;
}

interface InstructorStats {
  id: string;
  name: string;
  rating: number;
  totalClasses: number;
  totalStudents: number;
}

interface Expense {
  id: string;
  name: string;
  amount: number;
  type: 'fixed' | 'variable';
  frequency: 'monthly' | 'weekly' | 'one_time';
  category: string;
  date?: string;
  isRecurring: boolean;
}

interface UserRetention {
  renewed: number;
  notRenewed: number;
  expired: number;
  newUsers: number;
}

interface HourlyStats {
  hour: string;
  reservations: number;
}

const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

type TimePeriod = 'week' | 'month' | '3months' | 'year' | 'custom';

const getDateRange = (period: TimePeriod, customStart?: string, customEnd?: string): { start: string; end: string; label: string } => {
  const now = new Date();
  let start: Date;
  let end = now;
  let label = '';

  switch (period) {
    case 'week':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      label = 'Últimos 7 días';
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      label = 'Este mes';
      break;
    case '3months':
      start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      label = 'Últimos 3 meses';
      break;
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      label = 'Este año';
      break;
    case 'custom':
      start = customStart ? new Date(customStart) : new Date(now.getFullYear(), now.getMonth(), 1);
      end = customEnd ? new Date(customEnd) : now;
      label = 'Personalizado';
      break;
    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      label = 'Este mes';
  }

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
    label
  };
};

const StatisticsPage: React.FC = () => {
  const { businessId, currentBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'classes' | 'users' | 'finances'>('overview');

  // Time period filter
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');

  // Estados de datos
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [classPopularity, setClassPopularity] = useState<ClassPopularity[]>([]);
  const [instructorStats, setInstructorStats] = useState<InstructorStats[]>([]);
  const [userRetention, setUserRetention] = useState<UserRetention>({ renewed: 0, notRenewed: 0, expired: 0, newUsers: 0 });
  const [hourlyStats, setHourlyStats] = useState<HourlyStats[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);

  // Estados para formulario de gastos
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [expenseForm, setExpenseForm] = useState({
    name: '',
    amount: 0,
    type: 'fixed' as 'fixed' | 'variable',
    frequency: 'monthly' as 'monthly' | 'weekly' | 'one_time',
    category: 'general',
    isRecurring: true
  });

  // Cargar datos cuando cambie el negocio o el periodo
  useEffect(() => {
    if (businessId) {
      loadAllData();
    }
  }, [businessId, timePeriod, customDateStart, customDateEnd]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadMonthlyData(),
        loadClassPopularity(),
        loadInstructorStats(),
        loadUserRetention(),
        loadHourlyStats(),
        loadExpenses(),
        loadRevenue()
      ]);
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast.error('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos mensuales (últimos 6 meses)
  const loadMonthlyData = async () => {
    const months: MonthlyData[] = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = date.toISOString().split('T')[0];
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
      const monthName = date.toLocaleDateString('es-ES', { month: 'short' });

      // Contar usuarios creados en ese mes
      const usersQuery = query(collection(db, 'users'), where('businessId', '==', businessId));
      const usersSnapshot = await getDocs(usersQuery);
      let newUsersCount = 0;
      let totalUsersAtMonth = 0;

      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        let createdAt: string | null = null;
        if (data.createdAt?.toDate) {
          createdAt = data.createdAt.toDate().toISOString().split('T')[0];
        }
        if (createdAt && createdAt <= monthEnd) {
          totalUsersAtMonth++;
          if (createdAt >= monthStart) {
            newUsersCount++;
          }
        }
      });

      // Contar clases del mes
      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('isActive', '==', true)
      );
      const classesSnapshot = await getDocs(classesQuery);
      let classesCount = 0;

      classesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.date >= monthStart && data.date <= monthEnd) {
          classesCount++;
        }
      });

      // Calcular ingresos del mes (simplificado)
      const membershipsQuery = query(
        collection(db, 'userMemberships'),
        where('businessId', '==', businessId)
      );
      const membershipsSnapshot = await getDocs(membershipsQuery);

      // Cargar precios de membresías
      const membershipTypesQuery = query(collection(db, 'memberships'), where('businessId', '==', businessId));
      const membershipTypesSnapshot = await getDocs(membershipTypesQuery);
      const membershipPrices: { [id: string]: number } = {};
      membershipTypesSnapshot.docs.forEach(doc => {
        membershipPrices[doc.id] = doc.data().price || 0;
      });

      let monthRevenue = 0;
      membershipsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        let startDate: string | null = null;
        if (data.startDate?.toDate) {
          startDate = data.startDate.toDate().toISOString().split('T')[0];
        }
        if (startDate && startDate >= monthStart && startDate <= monthEnd) {
          monthRevenue += membershipPrices[data.membershipId] || 0;
        }
      });

      months.push({
        month: monthName,
        users: totalUsersAtMonth,
        revenue: monthRevenue,
        classes: classesCount,
        newUsers: newUsersCount
      });
    }

    setMonthlyData(months);
  };

  // Cargar popularidad de clases
  const loadClassPopularity = async () => {
    const classTypesQuery = query(collection(db, 'classTypes'), where('businessId', '==', businessId));
    const classTypesSnapshot = await getDocs(classTypesQuery);

    const classesQuery = query(
      collection(db, 'classes'),
      where('businessId', '==', businessId),
      where('isActive', '==', true)
    );
    const classesSnapshot = await getDocs(classesQuery);

    const popularity: { [key: string]: ClassPopularity } = {};

    classTypesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      popularity[doc.id] = {
        name: data.name,
        totalEnrollments: 0,
        averageOccupancy: 0,
        waitlistCount: 0
      };
    });

    let classCountByType: { [key: string]: number } = {};

    classesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const typeId = data.classType;
      if (popularity[typeId]) {
        const enrolled = Object.keys(data.enrolled || {}).length;
        const waitlist = (data.waitlist || []).length;

        popularity[typeId].totalEnrollments += enrolled;
        popularity[typeId].waitlistCount += waitlist;
        classCountByType[typeId] = (classCountByType[typeId] || 0) + 1;

        if (data.maxCapacity > 0) {
          const occupancy = (enrolled / data.maxCapacity) * 100;
          popularity[typeId].averageOccupancy += occupancy;
        }
      }
    });

    // Calcular promedios
    Object.keys(popularity).forEach(typeId => {
      if (classCountByType[typeId] > 0) {
        popularity[typeId].averageOccupancy = popularity[typeId].averageOccupancy / classCountByType[typeId];
      }
    });

    const sortedPopularity = Object.values(popularity)
      .sort((a, b) => b.totalEnrollments - a.totalEnrollments)
      .slice(0, 10);

    setClassPopularity(sortedPopularity);
  };

  // Cargar estadísticas de instructores
  const loadInstructorStats = async () => {
    const instructorsQuery = query(collection(db, 'instructors'), where('businessId', '==', businessId));
    const instructorsSnapshot = await getDocs(instructorsQuery);

    const stats: InstructorStats[] = instructorsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: `${data.firstName} ${data.lastName}`,
        rating: data.rating || 0,
        totalClasses: data.totalClasses || 0,
        totalStudents: data.totalStudents || 0
      };
    }).sort((a, b) => b.rating - a.rating);

    setInstructorStats(stats);
  };

  // Cargar retención de usuarios
  const loadUserRetention = async () => {
    const usersQuery = query(collection(db, 'users'), where('businessId', '==', businessId));
    const usersSnapshot = await getDocs(usersQuery);

    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();

    let renewed = 0;
    let notRenewed = 0;
    let expired = 0;
    let newUsers = 0;

    usersSnapshot.docs.forEach(doc => {
      const data = doc.data();

      // Usuarios nuevos (últimos 30 días)
      if (data.createdAt?.toDate) {
        const createdAt = data.createdAt.toDate().toISOString();
        if (createdAt >= thirtyDaysAgo) {
          newUsers++;
        }
      }

      // Analizar estado de membresía
      if (data.activeMembership || data.membershipType === 'monthly_fee') {
        if (data.paymentStatus === 'paid') {
          renewed++;
        } else {
          notRenewed++;
        }
      } else if (data.activePass) {
        if (data.activePass.creditsRemaining <= 0) {
          expired++;
        }
      } else {
        // Sin membresía activa
        notRenewed++;
      }
    });

    setUserRetention({ renewed, notRenewed, expired, newUsers });
  };

  // Cargar estadísticas por hora
  const loadHourlyStats = async () => {
    const classesQuery = query(
      collection(db, 'classes'),
      where('businessId', '==', businessId),
      where('isActive', '==', true)
    );
    const classesSnapshot = await getDocs(classesQuery);

    const hourlyCount: { [key: string]: number } = {};

    // Inicializar horas
    for (let i = 6; i <= 22; i++) {
      hourlyCount[`${i.toString().padStart(2, '0')}:00`] = 0;
    }

    classesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const time = data.time?.substring(0, 5);
      if (time && hourlyCount[time] !== undefined) {
        hourlyCount[time] += Object.keys(data.enrolled || {}).length;
      }
    });

    const stats: HourlyStats[] = Object.entries(hourlyCount).map(([hour, reservations]) => ({
      hour,
      reservations
    }));

    setHourlyStats(stats);
  };

  // Cargar gastos del periodo seleccionado
  const loadExpenses = async () => {
    try {
      const expensesQuery = query(collection(db, 'expenses'), where('businessId', '==', businessId));
      const expensesSnapshot = await getDocs(expensesQuery);

      const expensesData: Expense[] = expensesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Expense));

      setExpenses(expensesData);

      // Calcular total de gastos del periodo
      const { start: periodStart, end: periodEnd } = getDateRange(timePeriod, customDateStart, customDateEnd);
      const startDate = new Date(periodStart);
      const endDate = new Date(periodEnd);
      const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const monthsInPeriod = Math.max(1, daysDiff / 30);

      let total = 0;
      expensesData.forEach(expense => {
        if (expense.frequency === 'monthly') {
          total += expense.amount * monthsInPeriod;
        } else if (expense.frequency === 'weekly') {
          total += expense.amount * (daysDiff / 7);
        } else if (expense.frequency === 'one_time') {
          // Solo contar si la fecha del gasto está en el periodo
          if (expense.date && expense.date >= periodStart && expense.date <= periodEnd) {
            total += expense.amount;
          } else if (!expense.date) {
            // Si no tiene fecha, asumir que aplica
            total += expense.amount;
          }
        }
      });

      setTotalExpenses(Math.round(total));
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  // Cargar ingresos del periodo seleccionado
  const loadRevenue = async () => {
    const { start: periodStart, end: periodEnd } = getDateRange(timePeriod, customDateStart, customDateEnd);

    // Cargar precios de membresías
    const membershipTypesQuery = query(collection(db, 'memberships'), where('businessId', '==', businessId));
    const membershipTypesSnapshot = await getDocs(membershipTypesQuery);
    const membershipPrices: { [id: string]: number } = {};
    membershipTypesSnapshot.docs.forEach(doc => {
      membershipPrices[doc.id] = doc.data().price || 0;
    });

    // Ingresos de membresías
    const membershipsQuery = query(
      collection(db, 'userMemberships'),
      where('businessId', '==', businessId)
    );
    const membershipsSnapshot = await getDocs(membershipsQuery);

    let revenue = 0;
    membershipsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      let startDate: string | null = null;
      if (data.startDate?.toDate) {
        startDate = data.startDate.toDate().toISOString().split('T')[0];
      } else if (typeof data.startDate === 'string') {
        startDate = data.startDate.split('T')[0];
      }
      if (startDate && startDate >= periodStart && startDate <= periodEnd) {
        revenue += data.price || membershipPrices[data.membershipId] || 0;
      }
    });

    // Ingresos de bonos
    const usersQuery = query(collection(db, 'users'), where('businessId', '==', businessId));
    const usersSnapshot = await getDocs(usersQuery);

    const passTypesQuery = query(collection(db, 'passTypes'), where('businessId', '==', businessId));
    const passTypesSnapshot = await getDocs(passTypesQuery);
    const passPrices: { [id: string]: number } = {};
    passTypesSnapshot.docs.forEach(doc => {
      passPrices[doc.id] = doc.data().price || 0;
    });

    usersSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.activePass?.purchaseDate) {
        const purchaseDate = data.activePass.purchaseDate.split('T')[0];
        if (purchaseDate >= periodStart && purchaseDate <= periodEnd) {
          revenue += data.activePass.price || passPrices[data.activePass.passTypeId] || 0;
        }
      }
    });

    setTotalRevenue(revenue);
  };

  // Guardar gasto
  const handleSaveExpense = async () => {
    if (!businessId) return;

    if (!expenseForm.name.trim()) {
      toast.error('El nombre del gasto es requerido');
      return;
    }

    try {
      const expenseData = {
        ...expenseForm,
        businessId,
        updatedAt: serverTimestamp()
      };

      if (editingExpense) {
        await updateDoc(doc(db, 'expenses', editingExpense.id), expenseData);
        toast.success('Gasto actualizado');
      } else {
        await addDoc(collection(db, 'expenses'), {
          ...expenseData,
          createdAt: serverTimestamp()
        });
        toast.success('Gasto añadido');
      }

      resetExpenseForm();
      loadExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Error al guardar el gasto');
    }
  };

  // Eliminar gasto
  const handleDeleteExpense = async (expenseId: string) => {
    if (!confirm('¿Eliminar este gasto?')) return;

    try {
      await deleteDoc(doc(db, 'expenses', expenseId));
      toast.success('Gasto eliminado');
      loadExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Error al eliminar el gasto');
    }
  };

  // Reset formulario
  const resetExpenseForm = () => {
    setShowExpenseForm(false);
    setEditingExpense(null);
    setExpenseForm({
      name: '',
      amount: 0,
      type: 'fixed',
      frequency: 'monthly',
      category: 'general',
      isRecurring: true
    });
  };

  // Editar gasto
  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      name: expense.name,
      amount: expense.amount,
      type: expense.type,
      frequency: expense.frequency,
      category: expense.category,
      isRecurring: expense.isRecurring
    });
    setShowExpenseForm(true);
  };

  const netProfit = totalRevenue - totalExpenses;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Estadísticas | {currentBusiness?.name || 'Admin'}</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-amber-400">Estadísticas</h1>
            <p className="text-gray-400">Análisis detallado de tu negocio</p>
          </div>
          <Button onClick={loadAllData} variant="outline">
            <BarChart3 className="w-4 h-4 mr-2" />
            Actualizar datos
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'General', icon: BarChart3 },
            { id: 'classes', label: 'Clases', icon: Calendar },
            { id: 'users', label: 'Usuarios', icon: Users },
            { id: 'finances', label: 'Finanzas', icon: Euro }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${activeTab === tab.id
                ? 'bg-amber-500 text-black'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Time Period Selector */}
        <div className="flex flex-wrap items-center gap-3 bg-gray-900 p-3 rounded-xl border border-gray-800">
          <span className="text-gray-400 text-sm font-medium">Periodo:</span>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'week' as TimePeriod, label: '7 días' },
              { id: 'month' as TimePeriod, label: 'Este mes' },
              { id: '3months' as TimePeriod, label: '3 meses' },
              { id: 'year' as TimePeriod, label: 'Este año' },
              { id: 'custom' as TimePeriod, label: 'Personalizado' }
            ].map(period => (
              <button
                key={period.id}
                onClick={() => setTimePeriod(period.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${timePeriod === period.id
                  ? 'bg-amber-500 text-black'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
              >
                {period.label}
              </button>
            ))}
          </div>
          {timePeriod === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <input
                type="date"
                value={customDateStart}
                onChange={(e) => setCustomDateStart(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-2 py-1"
              />
              <span className="text-gray-500">-</span>
              <input
                type="date"
                value={customDateEnd}
                onChange={(e) => setCustomDateEnd(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-2 py-1"
              />
            </div>
          )}
          <span className="text-xs text-gray-500 ml-auto">
            {getDateRange(timePeriod, customDateStart, customDateEnd).label}
          </span>
        </div>

        {/* Tab: Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-gray-900 border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{userRetention.renewed + userRetention.notRenewed + userRetention.expired}</p>
                    <p className="text-xs text-gray-500">Usuarios totales</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-gray-900 border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                    <Euro className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{totalRevenue.toFixed(0)}€</p>
                    <p className="text-xs text-gray-500">Ingresos periodo</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-gray-900 border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{totalExpenses.toFixed(0)}€</p>
                    <p className="text-xs text-gray-500">Gastos periodo</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-gray-900 border-gray-800">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${netProfit >= 0 ? 'bg-green-600/20' : 'bg-red-600/20'} rounded-lg flex items-center justify-center`}>
                    {netProfit >= 0 ? <TrendingUp className="w-5 h-5 text-green-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {netProfit >= 0 ? '+' : ''}{netProfit.toFixed(0)}€
                    </p>
                    <p className="text-xs text-gray-500">Beneficio neto</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Gráfico de evolución mensual */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4">Evolución Mensual</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#f59e0b' }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="users" name="Usuarios" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="revenue" name="Ingresos (€)" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="classes" name="Clases" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Horarios más populares */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4">
                <Clock className="w-5 h-5 inline mr-2 text-amber-400" />
                Horarios Más Populares
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hourlyStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="hour" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  />
                  <Bar dataKey="reservations" name="Reservas" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        )}

        {/* Tab: Classes */}
        {activeTab === 'classes' && (
          <div className="space-y-6">
            {/* Clases más populares */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4">
                <Award className="w-5 h-5 inline mr-2 text-amber-400" />
                Clases Más Populares
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={classPopularity} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="name" type="category" stroke="#9ca3af" width={100} fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Bar dataKey="totalEnrollments" name="Inscripciones" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="waitlistCount" name="Lista espera" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Clases con más lista de espera */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4">
                <AlertTriangle className="w-5 h-5 inline mr-2 text-red-400" />
                Clases con Mayor Lista de Espera
              </h3>
              <div className="space-y-3">
                {classPopularity
                  .filter(c => c.waitlistCount > 0)
                  .sort((a, b) => b.waitlistCount - a.waitlistCount)
                  .slice(0, 5)
                  .map((cls, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center text-red-400 font-bold">
                          {index + 1}
                        </span>
                        <span className="text-white font-medium">{cls.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-red-400 font-bold">{cls.waitlistCount}</span>
                        <span className="text-gray-500 text-sm ml-1">en espera</span>
                      </div>
                    </div>
                  ))}
                {classPopularity.filter(c => c.waitlistCount > 0).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No hay usuarios en lista de espera</p>
                )}
              </div>
            </Card>

            {/* Ocupación media */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4">
                <Target className="w-5 h-5 inline mr-2 text-green-400" />
                Tasa de Ocupación por Clase
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {classPopularity.map((cls, index) => (
                  <div key={index} className="p-4 bg-gray-800 rounded-lg">
                    <p className="text-white font-medium mb-2">{cls.name}</p>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${cls.averageOccupancy >= 80 ? 'bg-green-500' :
                          cls.averageOccupancy >= 50 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                        style={{ width: `${Math.min(cls.averageOccupancy, 100)}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{cls.averageOccupancy.toFixed(0)}% ocupación media</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Tab: Users */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Retención de usuarios */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6 bg-gray-900 border-gray-800">
                <h3 className="text-lg font-semibold text-white mb-4">Estado de Usuarios</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Activos/Pagados', value: userRetention.renewed, color: '#10b981' },
                        { name: 'Pendientes', value: userRetention.notRenewed, color: '#f59e0b' },
                        { name: 'Expirados', value: userRetention.expired, color: '#ef4444' },
                        { name: 'Nuevos (30d)', value: userRetention.newUsers, color: '#3b82f6' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {[
                        { color: '#10b981' },
                        { color: '#f59e0b' },
                        { color: '#ef4444' },
                        { color: '#3b82f6' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-6 bg-gray-900 border-gray-800">
                <h3 className="text-lg font-semibold text-white mb-4">Métricas de Usuarios</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="text-gray-300">Usuarios Activos</span>
                    </div>
                    <span className="text-white font-bold">{userRetention.renewed}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="text-gray-300">Pendientes de Pago</span>
                    </div>
                    <span className="text-white font-bold">{userRetention.notRenewed}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <span className="text-gray-300">Membresías Expiradas</span>
                    </div>
                    <span className="text-white font-bold">{userRetention.expired}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-gray-300">Nuevos (últimos 30 días)</span>
                    </div>
                    <span className="text-white font-bold">{userRetention.newUsers}</span>
                  </div>
                  <div className="mt-4 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                    <p className="text-amber-400 text-sm">
                      <strong>Tasa de retención:</strong>{' '}
                      {userRetention.renewed + userRetention.notRenewed > 0
                        ? ((userRetention.renewed / (userRetention.renewed + userRetention.notRenewed)) * 100).toFixed(1)
                        : 0}%
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Instructores mejor valorados */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4">
                <Star className="w-5 h-5 inline mr-2 text-amber-400" />
                Instructores Mejor Valorados
              </h3>
              <div className="space-y-3">
                {instructorStats.slice(0, 5).map((instructor, index) => (
                  <div key={instructor.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${index === 0 ? 'bg-amber-500 text-black' :
                        index === 1 ? 'bg-gray-400 text-black' :
                          index === 2 ? 'bg-amber-700 text-white' :
                            'bg-gray-700 text-gray-300'
                        }`}>
                        {index + 1}
                      </span>
                      <span className="text-white font-medium">{instructor.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-400">{instructor.totalClasses} clases</span>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-white font-bold">{instructor.rating.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {instructorStats.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No hay datos de instructores</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Tab: Finances */}
        {activeTab === 'finances' && (
          <div className="space-y-6">
            {/* Resumen financiero */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-6 bg-gray-900 border-gray-800">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                  <span className="text-gray-400">Ingresos del Mes</span>
                </div>
                <p className="text-3xl font-bold text-green-400">{totalRevenue.toFixed(2)}€</p>
              </Card>
              <Card className="p-6 bg-gray-900 border-gray-800">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-red-400" />
                  <span className="text-gray-400">Gastos del Mes</span>
                </div>
                <p className="text-3xl font-bold text-red-400">{totalExpenses.toFixed(2)}€</p>
              </Card>
              <Card className={`p-6 border ${netProfit >= 0 ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'}`}>
                <div className="flex items-center gap-3 mb-2">
                  <Euro className={`w-6 h-6 ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-gray-400">Beneficio Neto</span>
                </div>
                <p className={`text-3xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {netProfit >= 0 ? '+' : ''}{netProfit.toFixed(2)}€
                </p>
              </Card>
            </div>

            {/* Gestión de gastos */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Gestión de Gastos</h3>
                <Button onClick={() => setShowExpenseForm(true)} size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir Gasto
                </Button>
              </div>

              {/* Formulario de gasto */}
              {showExpenseForm && (
                <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-white">
                      {editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
                    </h4>
                    <button onClick={resetExpenseForm} className="text-gray-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Nombre *</label>
                      <Input
                        value={expenseForm.name}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Ej: Alquiler local"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Importe (€) *</label>
                      <Input
                        type="number"
                        value={expenseForm.amount}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                        min={0}
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Frecuencia</label>
                      <select
                        value={expenseForm.frequency}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, frequency: e.target.value as any }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      >
                        <option value="monthly">Mensual</option>
                        <option value="weekly">Semanal</option>
                        <option value="one_time">Único</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Tipo</label>
                      <select
                        value={expenseForm.type}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, type: e.target.value as any }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      >
                        <option value="fixed">Fijo</option>
                        <option value="variable">Variable</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Categoría</label>
                      <select
                        value={expenseForm.category}
                        onChange={(e) => setExpenseForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      >
                        <option value="general">General</option>
                        <option value="rent">Alquiler</option>
                        <option value="utilities">Suministros</option>
                        <option value="salaries">Nóminas</option>
                        <option value="equipment">Equipamiento</option>
                        <option value="marketing">Marketing</option>
                        <option value="insurance">Seguros</option>
                        <option value="maintenance">Mantenimiento</option>
                        <option value="other">Otros</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleSaveExpense} className="w-full">
                        <Save className="w-4 h-4 mr-1" />
                        {editingExpense ? 'Actualizar' : 'Guardar'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de gastos */}
              <div className="space-y-2">
                {expenses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No hay gastos registrados. Añade tus gastos fijos y variables para calcular el beneficio neto.
                  </p>
                ) : (
                  expenses.map(expense => (
                    <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-8 rounded-full ${expense.type === 'fixed' ? 'bg-blue-500' : 'bg-amber-500'
                          }`} />
                        <div>
                          <p className="text-white font-medium">{expense.name}</p>
                          <p className="text-xs text-gray-500">
                            {expense.frequency === 'monthly' ? 'Mensual' :
                              expense.frequency === 'weekly' ? 'Semanal' : 'Único'} • {expense.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-red-400 font-bold">{expense.amount.toFixed(2)}€</span>
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="p-1 text-gray-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="p-1 text-gray-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Gráfico de ingresos vs gastos */}
            <Card className="p-6 bg-gray-900 border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4">Evolución de Ingresos</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" name="Ingresos (€)" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                  <Line type="monotone" dataKey="newUsers" name="Nuevos usuarios" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default StatisticsPage;




















